import { useEffect } from 'react';
import {
  Link,
  NavLink,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { Logo } from './components/Logo';
import { SyncIndicator } from './components/SyncIndicator';
import { type GroupSection, findGroup, groupPath, sectionOf } from './lib/groupPath';
import { canSee } from './lib/permissions';
import { isAdmin } from './lib/roles';
import type { Group, Section } from './lib/types';
import { Activity } from './pages/Activity';
import { Board } from './pages/Board';
import { Home } from './pages/Home';
import { InviteAccept } from './pages/InviteAccept';
import { JoinGroup } from './pages/JoinGroup';
import { Login } from './pages/Login';
import { AddTeam, Onboarding } from './pages/Onboarding';
import { Releases } from './pages/Releases';
import { Settings } from './pages/Settings';
import { LAST_GROUP_KEY, useStore } from './store/useStore';

export function App() {
  const ready = useStore((s) => s.ready);
  const user = useStore((s) => s.user);
  const bootstrap = useStore((s) => s.bootstrap);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  if (!ready) return <Splash />;

  // Signed out, the app is a public site: the marketing page, the two auth
  // screens, and the invite and join links people arrive on before they have an
  // account. Anything else redirects home rather than 404ing.
  if (!user) {
    return (
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<Login mode="login" />} />
        <Route path="/signup" element={<Login mode="register" />} />
        <Route path="/invite/:token" element={<InviteAccept />} />
        <Route path="/join/:code" element={<JoinGroup />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path="/invite/:token" element={<InviteAccept />} />
      <Route path="/join/:code" element={<JoinGroup />} />
      <Route path="/teams/new" element={<AddTeam />} />

      {/* Every board lives under the team that owns it, so a link, a bookmark and
          a second tab all name the team they mean. */}
      <Route path="/g/:groupKey/*" element={<GroupScope />} />

      {/* Bookmarks from before teams were addressable. */}
      <Route path="/releases" element={<LegacyRedirect section="releases" />} />
      <Route path="/settings" element={<LegacyRedirect section="settings" />} />

      <Route path="/" element={<TeamHome />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

/** The team `/` opens: the one this browser last used, else the first. */
function preferredGroup(groups: Group[]): Group | undefined {
  const last = localStorage.getItem(LAST_GROUP_KEY);
  return groups.find((group) => group.id === last) ?? groups[0];
}

/**
 * The bare origin carries no team, so it only decides where to go: onboarding for
 * an account with no team, otherwise the team this browser last had open.
 */
function TeamHome() {
  const groups = useStore((s) => s.groups);

  if (groups.length === 0) return <Onboarding />;

  const target = preferredGroup(groups);
  if (!target) return <Onboarding />;
  return <Navigate to={groupPath(target, landingOf(target))} replace />;
}

/**
 * The page a team opens on for this person: the one their layout names, which an
 * admin seeds and they can change afterwards. Falls back to the board only while
 * the membership list has not been fetched yet.
 */
function landingOf(group: Group): GroupSection {
  return group.layout?.landing ?? 'board';
}

function LegacyRedirect({ section }: { section: GroupSection }) {
  const groups = useStore((s) => s.groups);
  const target = preferredGroup(groups);
  return <Navigate to={target ? groupPath(target, section) : '/'} replace />;
}

/** A page this person has not been given. Same shape as the unknown-team card. */
function NoSectionAccess({ group }: { group: Group }) {
  const access = useStore((s) => s.access);
  const landing = access?.sections[0];

  return (
    <div className="card animate-rise mx-auto max-w-sm p-6 text-center">
      <p className="text-sm font-medium">Not your page</p>
      <p className="mt-1.5 text-sm leading-relaxed text-ink-faint">
        {landing
          ? 'An admin has not given your account access to this page in this team.'
          : 'An admin has not given your account access to any page in this team yet. Ask them to open one up for you.'}
      </p>
      {landing && (
        <Link to={groupPath(group, landing)} className="btn btn-primary mt-4 w-full">
          Go to {landing}
        </Link>
      )}
    </div>
  );
}

/**
 * Resolves the team in the URL and holds the store to it. Membership is not
 * decided here — the URL is only an address, and every request the pages make is
 * re-authorised against the session by the server.
 */
function GroupScope() {
  const { groupKey } = useParams<{ groupKey: string }>();
  const groups = useStore((s) => s.groups);
  const activeGroupId = useStore((s) => s.activeGroupId);
  const role = useStore((s) => s.role);
  const access = useStore((s) => s.access);
  const layout = useStore((s) => s.layout);
  const setActiveGroup = useStore((s) => s.setActiveGroup);

  const group = findGroup(groups, groupKey);
  const groupId = group?.id;

  useEffect(() => {
    if (groupId) void setActiveGroup(groupId);
  }, [groupId, setActiveGroup]);

  if (!group) return <UnknownTeam />;

  // Hold the splash until the store has switched over, so no page ever renders
  // one team's chrome around another team's rows.
  if (activeGroupId !== group.id) return <Splash />;

  const landing = layout.landing;

  // Which pages exist for this person is an admin's setting, so each one is a
  // guard rather than a plain route. The server re-checks every request behind
  // them; this only keeps the browser from showing a page it will be refused.
  const page = (section: Section, element: React.ReactNode) =>
    canSee(access, section) ? element : <NoSectionAccess group={group} />;

  return (
    <Shell group={group}>
      <Routes>
        <Route path="board" element={page('board', <Board />)} />
        <Route path="activity" element={page('activity', <Activity />)} />
        <Route path="releases" element={page('releases', <Releases />)} />
        <Route
          path="settings/*"
          element={
            isAdmin(role) ? <Settings /> : <Navigate to={groupPath(group, landing)} replace />
          }
        />
        <Route path="*" element={<Navigate to={landing} replace />} />
      </Routes>
    </Shell>
  );
}

/**
 * A team URL that resolves to no membership: a link to somebody else's team, a
 * team that was deleted, or one the user has been removed from since the page was
 * bookmarked. All three get the same answer, which is also the only answer that
 * does not disclose whether that team exists.
 */
function UnknownTeam() {
  const groups = useStore((s) => s.groups);
  const syncError = useStore((s) => s.syncError);
  const target = preferredGroup(groups);

  return (
    <div className="grid min-h-dvh place-items-center px-4">
      <div className="card animate-rise max-w-sm p-6 text-center">
        <p className="text-sm font-medium">This team isn't available to you</p>
        <p className="mt-1.5 text-sm leading-relaxed text-ink-faint">
          {syncError ??
            'Either it does not exist, or your account is not a member of it. Ask an owner for an invite or a join code.'}
        </p>
        {target ? (
          <Link to={groupPath(target)} className="btn btn-primary mt-4 w-full">
            Go to {target.name}
          </Link>
        ) : (
          <Link to="/" className="btn btn-primary mt-4 w-full">
            Continue
          </Link>
        )}
      </div>
    </div>
  );
}

function Splash() {
  return (
    <div className="grid min-h-dvh place-items-center">
      <div className="flex flex-col items-center gap-3">
        <span className="size-2.5 animate-pulse-dot rounded-full bg-accent" />
        <p className="text-sm text-ink-faint">Loading your workspace…</p>
      </div>
    </div>
  );
}

function Shell({ group, children }: { group: Group; children: React.ReactNode }) {
  const groups = useStore((s) => s.groups);
  const role = useStore((s) => s.role);
  const access = useStore((s) => s.access);
  const user = useStore((s) => s.user);
  const logout = useStore((s) => s.logout);
  const location = useLocation();
  const navigate = useNavigate();

  const section = sectionOf(location.pathname);

  // The navigation is the member's own: a page an admin has not given them is
  // not drawn at all, rather than drawn and then refused.
  const LABELS: Array<{ section: Section; label: string }> = [
    { section: 'board', label: 'Board' },
    { section: 'activity', label: 'Activity' },
    { section: 'releases', label: 'Releases' },
  ];
  const tabs: Array<{ section: GroupSection; label: string }> = [
    ...LABELS.filter((tab) => canSee(access, tab.section)),
    ...(isAdmin(role) ? [{ section: 'settings' as const, label: 'Settings' }] : []),
  ];

  /**
   * Switching teams stays on the page you were on — but only when the other team
   * actually gives you that page. Settings needs a role, and the three content
   * pages need a permission, and either can differ per team; when it does not
   * carry over, the other team's own starting page is the honest landing spot.
   */
  function switchTeam(groupId: string): void {
    const next = groups.find((candidate) => candidate.id === groupId);
    if (!next) return;

    const keep =
      section === 'settings'
        ? isAdmin(next.role)
        : (next.access?.sections as Section[] | undefined)?.includes(section as Section) ?? true;

    navigate(groupPath(next, keep ? section : landingOf(next)));
  }

  return (
    <div className="min-h-dvh">
      <header className="sticky top-0 z-20 border-b border-line bg-canvas/85 backdrop-blur-md">
        <div className="mx-auto flex h-14 max-w-7xl items-center gap-3 px-4">
          <div className="flex items-center gap-2.5">
            <Logo />
            <span className="hidden text-sm font-semibold tracking-tight sm:block">
              Project-Workflow
            </span>
          </div>

          <div className="flex items-center gap-1">
            {groups.length > 1 ? (
              <select
                value={group.id}
                onChange={(event) => switchTeam(event.target.value)}
                className="field-input max-w-44 !py-1.5 text-sm"
                aria-label="Active team"
              >
                {groups.map((candidate) => (
                  <option key={candidate.id} value={candidate.id}>
                    {candidate.name}
                  </option>
                ))}
              </select>
            ) : (
              <span className="truncate text-sm text-ink-soft">{group.name}</span>
            )}

            <Link
              to="/teams/new"
              className="btn btn-ghost !px-2"
              title="Create or join another team"
            >
              <svg viewBox="0 0 16 16" className="size-4" fill="currentColor" aria-hidden="true">
                <path d="M8 3a.75.75 0 0 1 .75.75v3.5h3.5a.75.75 0 0 1 0 1.5h-3.5v3.5a.75.75 0 0 1-1.5 0v-3.5h-3.5a.75.75 0 0 1 0-1.5h3.5v-3.5A.75.75 0 0 1 8 3Z" />
              </svg>
              <span className="sr-only">Create or join another team</span>
            </Link>
          </div>

          <nav className="ml-2 flex items-center gap-1">
            {tabs.map((tab) => (
              <NavLink
                key={tab.section}
                to={groupPath(group, tab.section)}
                className={({ isActive }) =>
                  `rounded-lg px-3 py-1.5 text-sm transition-colors ${
                    isActive
                      ? 'bg-elevated font-medium text-ink'
                      : 'text-ink-soft hover:bg-hover hover:text-ink'
                  }`
                }
              >
                {tab.label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex items-center gap-2">
            <SyncIndicator />
            <div className="hidden text-right leading-tight sm:block">
              <p className="text-xs font-medium">{user?.displayName}</p>
              <p className="text-[11px] text-ink-faint capitalize">{role}</p>
            </div>
            <button type="button" onClick={() => void logout()} className="btn btn-ghost !px-2.5">
              <svg viewBox="0 0 16 16" className="size-4" fill="currentColor" aria-hidden="true">
                <path d="M6 2.5a.75.75 0 0 1 0 1.5H4.5v8H6a.75.75 0 0 1 0 1.5H4.5A1.5 1.5 0 0 1 3 12V4a1.5 1.5 0 0 1 1.5-1.5H6Zm4.22 2.47a.75.75 0 0 1 1.06 0l2.25 2.25a.75.75 0 0 1 0 1.06l-2.25 2.25a.75.75 0 1 1-1.06-1.06l.97-.97H7.75a.75.75 0 0 1 0-1.5h3.44l-.97-.97a.75.75 0 0 1 0-1.06Z" />
              </svg>
              <span className="sr-only">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-4 py-6">{children}</main>
    </div>
  );
}
